import React, { useRef } from 'react';
import { IFileItem } from '../../../types/api/FileItem';
import { DraggableAttachedFileListItem } from './partials/DraggableAttachedFileBaseListItem';
import { useWebsocketSyncedAttachments } from './useWebsocketSyncedAttachments';
import bem from '../../../lib/bemUtils/bem';
import ContentMessage from '../ContentMessage/ContentMessage';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { Modifier } from '../../../lib/bemUtils/Modifier';
import { Help } from '../../Form2/partials/Help/Help';
import { useSortableList } from '../SortableContentList/useSortableList';
import { ContentList } from '../ContentList';
import { SortableListContextProvider } from '../../DragAndDrop';
import { SonForm } from '../../Form2';
import { AnyParameter } from '../../Form2/types/SonForm';
import { ContentListModifier } from '../ContentList/ContentList';

export enum AttachedFilesBaseSortableListModifier {
  PADDING = 'padding',
}

export type AttachedFilesBaseSortableList = {
  attachedFileUpdateOrder: SonForm<AnyParameter> | undefined;
  headline: string;
  headlineModifier?: Modifier<AttachedFilesBaseSortableListModifier>;
  hideNoAttachmentsMessage?: boolean;
  backgroundTransparent?: boolean;
  attachments?: IFileItem[];
  postId?: string;
};

export function AttachedFilesBaseSortableList({
  attachedFileUpdateOrder,
  attachments,
  backgroundTransparent,
  headline,
  headlineModifier,
  hideNoAttachmentsMessage = false,
  postId,
}: AttachedFilesBaseSortableList) {
  const { syncedAttachments, setSyncedAttachments } = useWebsocketSyncedAttachments(attachments, postId);
  const headerRef = useRef<HTMLHeadingElement | null>(null);
  const { t } = useSonTranslation('AttachedFiles');
  const { sortableItems, ...context } = useSortableList(
    [syncedAttachments, setSyncedAttachments],
    attachedFileUpdateOrder
  );

  if (syncedAttachments.length === 0) {
    return hideNoAttachmentsMessage ? null : <ContentMessage text={t('noAttachments')} />;
  }

  return (
    <>
      <div className={bem('attached-files-base__headline-container', headlineModifier)}>
        <h4 ref={headerRef} className="attached-files-base__headline font-bold-15-19">
          {headline}
        </h4>
        <Help id="attached-files-base-sortable-list-form-help" helpText={t('helptext')} />
      </div>
      <SortableListContextProvider {...context}>
        <ContentList modifier={backgroundTransparent ? ContentListModifier.BACKGROUND_TRANSPARENT : null}>
          {sortableItems.map((attachment: IFileItem) => (
            <DraggableAttachedFileListItem
              // new list length => force rerender to ensure context menu entries are updated
              // -> submit sent old sorted list
              // SIBZIEL-13171, SIBZIEL-13166
              key={attachment.id + syncedAttachments.length}
              {...attachment}
              headerRef={headerRef}
            />
          ))}
        </ContentList>
      </SortableListContextProvider>
    </>
  );
}
